/**
 * Sessions API Client
 *
 * API client functions for all Session-related endpoints.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 */

import { transformToCamelCaseDeep } from '@gw/ui';

import { BaseApiClient } from './baseApiClient';

import type { ApplicationSession, ListSessionsResponse, SessionFilters } from '@gw/ui/types';

// Extended types for session creation
export interface CreateSessionResponse {
  session_id: string;
  session_jwt: string;
  launch_url: string;
  expires_at: number; // Unix timestamp in seconds from backend
  tokens_consumed: number;
  application_name: string;
  is_test?: boolean; // GW-358: Test session flag
  // Container app fields (optional, present when session_type === "container")
  session_type?: 'container';
  container_session_id?: string;
  access_mode?: 'TERMINAL' | 'WEB_UI';
  container_status?: string;
}

interface CreateSessionApiRequest {
  application_id: string;
  duration_minutes: number;
  is_test?: boolean; // GW-358: Optional flag for test sessions
  // GW-9255: Optional publisher/test launch URL override. The backend (GW-9254)
  // accepts this in camelCase (`launchUrlOverride`) even though the sibling fields
  // are snake_case, and builds the returned launch_url from it + the session JWT.
  launchUrlOverride?: string;
}

// GW-554: Response type for session renewal/extension
interface RenewSessionResponse {
  session_id: string;
  new_expires_at: string;
  additional_cost: number;
  total_tokens_spent: number;
}

// GW-2511: Session extension modal types

export interface ExtensionCostResponse {
  sessionId: string;
  extensionCostTokens: number;
  baseCostTokens: number;
  durationMinutes: number;
}

export interface SessionBalanceResponse {
  sessionId: string;
  organizationId: string;
  balanceTokens: number;
  currency: string;
}

interface ExtendSessionResponse {
  sessionId: string;
  newExpiresAt: string;
  extensionCostTokens: number;
  idempotencyKey: string;
}

export interface SessionPurchaseItem {
  purchaseId: string;
  sessionId: string;
  itemId: string;
  itemName: string;
  totalTokens: number;
  status: string;
  createdAt: string;
}

interface SessionPurchasesResponse {
  sessionId: string;
  purchases: SessionPurchaseItem[];
  total: number;
}

class SessionsApiClient extends BaseApiClient {
  // ============================================================================
  // Public API Methods
  // ============================================================================

  /**
   * Create a new session for an application
   *
   * HTTP Method: POST /sessions
   * Authorization: Required (Cognito JWT)
   *
   * @param applicationId - ID of the application to create session for
   * @param durationMinutes - Session duration (15, 30, or 60 minutes)
   * @param isTest - GW-358: Optional flag to create test session (no tokens charged)
   * @param launchUrlOverride - GW-9255: Optional launch URL override; the backend
   *   builds the returned launch_url from this base plus the session JWT querystring.
   * @returns Created session details with JWT and launch URL
   * @throws {ApiError} When API request fails
   */
  async createSession(
    applicationId: string,
    durationMinutes: number,
    isTest?: boolean,
    launchUrlOverride?: string
  ): Promise<CreateSessionResponse> {
    const request: CreateSessionApiRequest = {
      application_id: applicationId,
      duration_minutes: durationMinutes,
      ...(isTest !== undefined && { is_test: isTest }), // GW-358: Include if provided
      // GW-9255: Only include a non-empty override (camelCase per GW-9254 backend)
      ...(launchUrlOverride?.trim() && { launchUrlOverride: launchUrlOverride.trim() }),
    };
    return this.post<CreateSessionResponse>('/sessions', request);
  }

  /**
   * List user's sessions with pagination support
   *
   * HTTP Method: GET /sessions
   * Authorization: Required (Cognito JWT)
   *
   * Query Parameters:
   * - limit: Number of items per page (default 20, max 100)
   * - cursor: Base64-encoded pagination cursor from previous response
   * - search: Search query for application name or session ID
   * - status: Filter by session status (active, expired, revoked)
   *
   * @param filters - Optional filters and pagination parameters
   * @returns List of sessions with pagination metadata
   * @throws {ApiError} When API request fails
   */
  async listSessions(filters?: SessionFilters): Promise<ListSessionsResponse> {
    const queryParams = filters
      ? this.buildQueryString(filters as Record<string, string | number>)
      : '';
    const response = await this.get<ListSessionsResponse>(`/sessions${queryParams}`);
    // GW-6662: Hydrate snake_case container fields to camelCase
    return transformToCamelCaseDeep(response) as ListSessionsResponse;
  }

  /**
   * Get session details by ID
   *
   * HTTP Method: GET /sessions/{id}
   * Authorization: Required (Cognito JWT)
   *
   * @param sessionId - Session ID
   * @returns Session details
   * @throws {ApiError} When API request fails
   */
  async getSession(sessionId: string): Promise<ApplicationSession> {
    const response = await this.get<ApplicationSession>(`/sessions/${sessionId}`);
    // GW-6662: Hydrate snake_case container fields to camelCase
    return transformToCamelCaseDeep(response) as ApplicationSession;
  }

  /**
   * Revoke an active session
   *
   * HTTP Method: DELETE /sessions/{id}
   * Authorization: Required (Cognito JWT)
   *
   * @param sessionId - Session ID to revoke
   * @throws {ApiError} When API request fails
   */
  async revokeSession(sessionId: string): Promise<void> {
    return this.delete<void>(`/sessions/${sessionId}`);
  }

  /**
   * Renew/extend an active session
   * GW-554: Wire up extend session to backend API
   *
   * HTTP Method: PUT /sessions/{id}/renew
   * Authorization: Required (Cognito JWT)
   *
   * @param sessionId - Session ID to renew
   * @param additionalMinutes - Number of minutes to extend (1-1440)
   * @returns Renewal details including new expiration and cost
   * @throws {ApiError} When API request fails
   *   - 402: Insufficient token balance
   *   - 400: Session not active
   *   - 404: Session not found
   *   - 410: Session expired or revoked
   */
  async renewSession(sessionId: string, additionalMinutes: number): Promise<RenewSessionResponse> {
    return this.put<RenewSessionResponse>(`/sessions/${sessionId}/renew`, {
      additional_minutes: additionalMinutes,
    });
  }

  /**
   * Get extension cost for a session
   * GW-2511: Session extension modal
   *
   * HTTP Method: GET /sessions/{id}/extension-cost
   * Authorization: Required (Cognito JWT)
   */
  async getExtensionCost(sessionId: string): Promise<ExtensionCostResponse> {
    return this.get<ExtensionCostResponse>(`/sessions/${sessionId}/extension-cost`);
  }

  /**
   * Get current token balance for a session context
   * GW-2511: Session extension modal
   *
   * HTTP Method: GET /sessions/{id}/balance
   * Authorization: Required (Cognito JWT)
   */
  async getSessionBalance(sessionId: string): Promise<SessionBalanceResponse> {
    return this.get<SessionBalanceResponse>(`/sessions/${sessionId}/balance`);
  }

  /**
   * Extend an active session
   * GW-2511: Session extension modal
   *
   * HTTP Method: POST /sessions/{id}/extend
   * Authorization: Required (Cognito JWT)
   *
   * @param sessionId - Session ID to extend
   * @param idempotencyKey - UUID for idempotent retry safety
   */
  async extendSession(sessionId: string, idempotencyKey: string): Promise<ExtendSessionResponse> {
    return this.post<ExtendSessionResponse>(`/sessions/${sessionId}/extend`, { idempotencyKey });
  }

  /**
   * Get purchase history for a session
   * GW-2511: Session detail view purchase list
   *
   * HTTP Method: GET /sessions/{id}/purchases
   * Authorization: Required (Cognito JWT)
   */
  async getSessionPurchases(sessionId: string): Promise<SessionPurchasesResponse> {
    return this.get<SessionPurchasesResponse>(`/sessions/${sessionId}/purchases`);
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const sessionsApi = new SessionsApiClient();
